<template>
  <div class="sms-credits-tab">
    <!-- Tab header — the design language's eyebrow / display title / lede block, identical in
         construction and class name to MyAccount.vue's Team header (.ma-tabhead) so the two tabs
         open the same way. Above the upsell on purpose: it identifies the tab for every site,
         texting-enabled or not. -->
    <div class="ma-tabhead">
      <div class="ma-tabhead__eyebrow">Credits</div>
      <h3 class="ma-tabhead__title">Your credit balances</h3>
      <p class="ma-tabhead__lede">
        What you have left to send with, what you&rsquo;ve used this month, and how to add more.
      </p>
    </div>

    <!-- Preview/upsell for sites WITHOUT texting enabled. Static — no wallet query is made. -->
    <div v-if="!smsFeatureEnabled" class="sct-upsell">
      <div class="sct-upsell__icon"><i class="la la-comment-dots"></i></div>
      <div class="sct-upsell__body">
        <h4 class="sct-upsell__title">Enable 2-Way SMS</h4>
        <p class="sct-upsell__desc">
          Text appointment reminders, recalls, and campaigns to your patients —
          {{ previewCredits.toLocaleString() }} free credits every month, plus low-cost top-ups.
          Here&rsquo;s a preview of what you&rsquo;d get.
        </p>
        <FeatureRequestButton
          class="sct-upsell__cta"
          feature-key="SMS"
          feature-label="2-Way SMS texting"
          source-page="My Account - SMS credits"
        />
        <FeatureRequestNote feature="SMS" />
      </div>
    </div>

    <!-- Low-balance warning — SMS sites with auto-reload OFF running near empty.
         Never shown in preview mode (showLowBalanceBanner requires smsFeatureEnabled). -->
    <b-alert :show="showLowBalanceBanner" variant="warning" class="sct-lowbal">
      <i class="la la-exclamation-triangle mr-2"></i>Your SMS credits are low and auto-reload is
      off — appointment reminders and patient texts may stop going through. Turn on auto-reload
      above, or add credits.
    </b-alert>

    <!-- ==================== Per-channel balances (SMS · Email · AI later) ==================== -->
    <!-- Row 1 — the two questions a practice actually arrives with, side by side:
         "how many credits do I have" (balances) and "where did they go" (usage). -->
    <b-row>
      <!-- Preview-lock lives on the BALANCES column now, not the row: an IV-only (no-texting)
           site can render the usage card below without SMS, and it must never be dimmed/locked.
           The balances card still shows its illustrative locked preview when texting is off. -->
      <!-- Full-width on purpose (Saumil, 2026-08-19 "layout C"): at card width ≥ ~700px the
           auto-fit tile grid puts all three balance tiles side by side in one row. -->
      <b-col cols="12" class="mb-4" :class="{ 'sct-preview-lock': !smsFeatureEnabled }">
        <b-card class="h-100 sct-card">
          <div class="sct-card__head mb-2">
            <h4 class="sct-card__title mb-0">
              <i class="la la-wallet mr-2"></i>Balances
            </h4>
            <b-spinner v-if="loading && !hasBalance" small class="text-muted" />
          </div>

          <!-- Stat-tile grid (the PatientPayments .pp-stat anatomy). PRESENTATION ONLY: every
               value below is the exact binding the previous stacked rows rendered, and every
               v-if is unchanged — the rows became tiles, nothing about what is shown or where it
               comes from moved. auto-fit/minmax lets the tiles reflow to one column as the card
               narrows rather than overflowing it (this card is half-width above lg). -->
          <div class="sct-stats">
            <!-- SMS & MMS -->
            <div class="sct-chan">
              <div class="sct-chan__label">
                <div class="sct-chan__ic sct-chan__ic--sms"><ChannelIcon channel="sms" /></div>
                <div class="sct-chan__name">SMS &amp; MMS</div>
              </div>
              <div class="sct-chan__bal">{{ dispCreditsAvailable.toLocaleString() }}</div>
              <div class="sct-chan__sub"><b>{{ dispFreeRemaining.toLocaleString() }}</b> of {{ dispFreeAllowance.toLocaleString() }} free<template v-if="dispPaidCredits > 0"> · <b>{{ dispPaidCredits.toLocaleString() }}</b> purchased</template><template v-if="smsFeatureEnabled && resetDate"> · resets {{ resetDate | dateformat(dateFormatStr) }}</template></div>
              <div class="sct-chan__unit">1 credit = 1 text segment</div>
              <!-- Free-allowance bar: the "N of M free" line above, drawn. Only SMS gets one —
                   it is the pool a practice actually runs down mid-month. -->
              <div class="sct-chan__bar">
                <div class="sct-chan__barfill" :style="{ width: smsFreeRemainingPct + '%' }"></div>
              </div>
            </div>

            <!-- Email (separate 10k/mo pool, shown once the wallet doc carries it). Display-only
                 until VUE_APP_EMAIL_WALLET_ENABLED — then the "included" tag drops (it's a real
                 purchasable pool) and the "Manage Email credits" section below goes live. -->
            <div v-if="emailChannel" class="sct-chan">
              <div class="sct-chan__label">
                <div class="sct-chan__ic sct-chan__ic--email"><ChannelIcon channel="email" /></div>
                <div class="sct-chan__name">Email <span v-if="!emailWalletEnabled" class="sct-chan__tag sct-chan__tag--email">included</span></div>
              </div>
              <div class="sct-chan__bal">{{ emailAvailable.toLocaleString() }}</div>
              <div class="sct-chan__sub"><b>{{ emailFreeRemaining.toLocaleString() }}</b> of {{ emailFreeAllowance.toLocaleString() }} free<template v-if="emailWalletEnabled && emailPaidCredits > 0"> · <b>{{ emailPaidCredits.toLocaleString() }}</b> purchased</template><template v-if="emailResetDate"> · resets {{ emailResetDate | dateformat(dateFormatStr) }}</template></div>
              <div class="sct-chan__unit">1 credit = 1 email</div>
            </div>

            <!-- AI (future channel — reserved tile) -->
            <div class="sct-chan sct-chan--muted">
              <div class="sct-chan__label">
                <div class="sct-chan__ic sct-chan__ic--ai"><ChannelIcon channel="ai" /></div>
                <div class="sct-chan__name">AI minutes <span class="sct-chan__tag">Coming soon</span></div>
              </div>
              <div class="sct-chan__bal sct-chan__bal--muted">—</div>
              <div class="sct-chan__sub">Voice &amp; text AI receptionist credits will live here too</div>
            </div>
          </div>
        </b-card>
      </b-col>

      <!-- Usage report — sits beside the balances on purpose: "how many credits do I have"
           and "where did they go" are the same question, so they share the top row. Distinct
           from Credit activity (money IN: top-ups/reloads); this is per-message CONSUMPTION.
           Its month total is the SAME number the header pill shows, by construction. -->
      <!-- Usage follows full-width beneath the balances row — a lone half-width card would dangle. -->
      <b-col v-if="showUsageCard" id="usage-report" cols="12" class="mb-4">
        <b-card class="h-100 sct-card">
          <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
            <h4 class="sct-card__title mb-0">
              <i class="la la-chart-bar mr-2"></i>Usage report
            </h4>
            <div class="sct-usage__monthpick">
              <label for="usage-month" class="mr-2 mb-0">Month</label>
              <select id="usage-month" v-model="usageMonthSel" class="sct-usage__select" @change="reloadUsage">
                <option v-for="m in usageMonthOptions" :key="m.value || 'current'" :value="m.value">{{ m.label }}</option>
              </select>
            </div>
          </div>

          <div v-if="usageSummaryLoading && !anyUsageLoaded" class="text-muted text-center py-3">
            <b-spinner small class="mr-1" />Loading…
          </div>

          <template v-else>
            <div class="sct-usage__grid">
            <!-- Wallet channels (SMS · Email) render only for a texting-enabled site; an IV-only
                 site has no wallet channel and must not show a phantom empty "SMS & MMS" row. -->
            <template v-if="smsFeatureEnabled">
            <div v-for="ch in usageChannels" :key="ch.key" class="sct-usage__channel">
              <div class="sct-usage__chhead">
                <span class="sct-usage__chname">{{ ch.label }}</span>
                <span v-if="usageHeadline(ch.key)" class="sct-usage__chline">{{ usageHeadline(ch.key) }}</span>
              </div>

              <div v-if="!usageRows(ch.key).length" class="text-muted py-2 sct-usage__empty">
                No {{ ch.label.toLowerCase() }} usage in this period.
              </div>

              <div v-else class="sct-usage__bars">
                <button
                  v-for="row in usageRows(ch.key)"
                  :key="ch.key + row.feature + row.direction + row.sendMode"
                  type="button"
                  class="sct-usage__row"
                  :title="'Show these messages'"
                  @click="openUsageLog(ch.key, row)"
                >
                  <span class="sct-usage__label">{{ featureLabel(row) }}</span>
                  <span class="sct-usage__bar">
                    <span class="sct-usage__fill" :style="{ width: usageBarWidth(ch.key, row) }"></span>
                  </span>
                  <span class="sct-usage__num">{{ row.credits.toLocaleString() }}</span>
                  <span class="sct-usage__msgs">{{ row.messages.toLocaleString() }} msg</span>
                </button>
              </div>

              <!-- Puerto Rico sub-total (SMS only). The PR numbers inside the breakdown above bill
                   5 credits per segment, so a practice with PR patients draws more than its message
                   count suggests — name it here rather than leave it implicit. Hidden entirely when
                   there is no PR traffic, which is most sites. -->
              <div v-if="ch.key === 'sms' && usagePr" class="sct-usage__pr">
                <span class="sct-usage__pr-label"><i class="la la-map-marker mr-1" aria-hidden="true"></i>Puerto Rico numbers</span>
                <span class="sct-usage__pr-nums">{{ usagePr.messages.toLocaleString() }} {{ usagePr.messages === 1 ? 'message' : 'messages' }} &middot; {{ usagePr.credits.toLocaleString() }} credits</span>
                <span class="sct-usage__pr-note">({{ prSegmentCredits }} credits per text segment)</span>
              </div>
            </div>
            </template>

            <!-- Insurance Verification usage (own markup — IV is not a wallet channel). Renders
                 whenever the section is visible: flag ON, or the site has spent history. -->
            <div v-if="ivUsageVisible" class="sct-ivu">
              <div class="sct-usage__chhead">
                <span class="sct-usage__chname">Insurance Verification</span>
                <span v-if="ivHasUsage" class="sct-usage__chline">{{ ivMeter.headline }} {{ ivMeter.unit }}</span>
                <span class="sct-ivu__month">{{ usageMonthLabel }}</span>
              </div>

              <div v-if="ivUsagePanelLoading && !ivUsagePanelData" class="text-muted py-2 sct-usage__empty">
                <b-spinner small class="mr-1" />Loading…
              </div>

              <div v-else-if="!ivHasUsage" class="text-muted py-2 sct-usage__empty">
                No insurance verification checks in this period.
              </div>

              <template v-else>
                <div class="sct-usage__bars">
                  <button
                    v-for="entry in ivMeter.split"
                    :key="'iv-' + entry.key"
                    type="button"
                    class="sct-usage__row sct-ivu__row"
                    :title="'Show these checks'"
                    @click="openIvLog(entry)"
                  >
                    <span class="sct-usage__label">{{ entry.label }}</span>
                    <span class="sct-usage__bar">
                      <span class="sct-usage__fill" :style="{ width: ivBarWidth(entry) }"></span>
                    </span>
                    <span class="sct-usage__num">{{ ivEntryCount(entry).toLocaleString() }}</span>
                  </button>
                </div>

                <!-- Refused-free checks are never persisted (nothing to drill into), so this line
                     is not clickable. -->
                <div v-if="ivMeter.free > 0" class="sct-ivu__free">
                  <span class="sct-ivu__free-label"><i class="la la-shield-alt mr-1" aria-hidden="true"></i>Refused free</span>
                  <span class="sct-ivu__free-num">{{ ivMeter.free.toLocaleString() }}</span>
                </div>

                <p v-if="ivMeter.unknownNote" class="sct-ivu__note text-muted mb-0">{{ ivMeter.unknownNote }}</p>
              </template>
            </div>

            </div><!-- /.sct-usage__grid -->

            <!-- Drill-down: the per-message log for whichever row was clicked. Deliberately OUTSIDE
                 the two-column grid — the table needs the full width. -->
            <div v-if="usageLogOpen" class="sct-usage__log">
              <div class="sct-usage__loghead">
                <span>
                  {{ usageLogTitle }}
                  <span class="sct-usage__logcount">{{ usageLog.total.toLocaleString() }} messages</span>
                </span>
                <button type="button" class="sct-usage__close" @click="closeUsageLog">
                  <i class="la la-times" aria-hidden="true"></i> Close
                </button>
              </div>

              <div v-if="usageLogLoading" class="text-muted text-center py-3">
                <b-spinner small class="mr-1" />Loading…
              </div>
              <div v-else-if="!usageLog.rows.length" class="text-muted py-2">No messages match.</div>
              <table v-else class="sct-usage__table">
                <thead>
                  <tr><th>Date</th><th>Feature</th><th>Dir</th><th class="text-right">Credits</th></tr>
                </thead>
                <tbody>
                  <tr v-for="r in usageLog.rows" :key="r.id">
                    <td>{{ formatUsageLogDate(r.createdAt) }}</td>
                    <td>{{ r.feature }}<span v-if="r.region === 'PR'" class="sct-usage__prbadge" title="Puerto Rico number — 5 credits per segment">PR</span></td>
                    <td>{{ r.direction === 'in' ? 'In' : 'Out' }}</td>
                    <td class="text-right">{{ r.segments }}</td>
                  </tr>
                </tbody>
              </table>

              <div v-if="usageLog.total > usageLog.rows.length || usageLog.page > 1" class="sct-usage__pager">
                <b-button size="sm" variant="outline-secondary" :disabled="usageLog.page <= 1 || usageLogLoading" @click="usagePage(-1)">Previous</b-button>
                <span class="mx-2 text-muted">Page {{ usageLog.page }}</span>
                <b-button size="sm" variant="outline-secondary" :disabled="!usageLog.hasMore || usageLogLoading" @click="usagePage(1)">Next</b-button>
              </div>
            </div>

            <!-- Insurance Verification drill-down: the per-check log for whichever trigger bar was
                 clicked. Full-width below the grid, mirroring the SMS log above. Reads the
                 insuranceVerification store via iv-prefixed getters (the local usageLog* names
                 above belong to the wallet SMS log — a coincidence, not the same state). -->
            <div v-if="ivLogOpen" class="sct-usage__log">
              <div class="sct-usage__loghead">
                <span>
                  Insurance checks — {{ ivTriggerLabelFor(ivLogTrigger) }}
                  <span class="sct-usage__logcount">{{ ivLogTotal.toLocaleString() }} checks</span>
                </span>
                <button type="button" class="sct-usage__close" @click="ivCloseUsageLog">
                  <i class="la la-times" aria-hidden="true"></i> Close
                </button>
              </div>

              <div v-if="ivLogLoading" class="text-muted text-center py-3">
                <b-spinner small class="mr-1" />Loading…
              </div>
              <div v-else-if="!ivLogRows.length" class="text-muted py-2">No checks match.</div>
              <table v-else class="sct-usage__table">
                <thead>
                  <tr><th>Date</th><th>Patient</th><th>Trigger</th><th>Result</th><th>Billed</th></tr>
                </thead>
                <tbody>
                  <tr v-for="c in ivLogCells" :key="c.key">
                    <td>{{ formatUsageLogDate(c.checkedAt) }}</td>
                    <td>{{ c.patient }}</td>
                    <td>{{ c.trigger }}</td>
                    <td>{{ c.result }}</td>
                    <td>{{ c.billed }}</td>
                  </tr>
                </tbody>
              </table>

              <div v-if="ivLogTotal > ivLogRows.length || ivLogPage > 1" class="sct-usage__pager">
                <b-button size="sm" variant="outline-secondary" :disabled="ivLogPage <= 1 || ivLogLoading" @click="ivUsagePage(-1)">Previous</b-button>
                <span class="mx-2 text-muted">Page {{ ivLogPage }}</span>
                <b-button size="sm" variant="outline-secondary" :disabled="!ivLogHasMore || ivLogLoading" @click="ivUsagePage(1)">Next</b-button>
              </div>
            </div>
          </template>
        </b-card>
      </b-col>
    </b-row>

    <!-- Section labels, paired so SMS sits above SMS controls and Email above Email. -->
    <b-row v-if="smsFeatureEnabled" class="sct-headrow">
      <b-col cols="12" lg="6">
        <div class="sct-section-head">
          <span>Manage SMS credits</span>
          <span class="sct-section-soon">{{ emailWalletEnabled ? 'AI top-ups coming soon' : 'Email &amp; AI top-ups coming soon' }}</span>
        </div>
      </b-col>
      <b-col v-if="emailManageVisible" cols="12" lg="6">
        <div class="sct-section-head">
          <span>Manage Email credits</span>
          <span class="sct-section-soon">$0.005 per email credit</span>
        </div>
      </b-col>
    </b-row>

    <!-- Every control paired with its opposite-channel twin: buy | buy,
         auto-reload | auto-reload. Payment method is shared by both channels, so it
         pairs with Credit activity rather than with a channel control. -->
    <b-row :class="{ 'sct-preview-lock': !smsFeatureEnabled }">
      <!-- ========================== Buy credits ========================== -->
      <b-col cols="12" lg="6" class="mb-4">
        <b-card class="h-100 sct-card">
          <h4 class="sct-card__title mb-3">
            <i class="la la-plus-circle mr-2"></i>Buy credits
          </h4>

          <b-alert :show="!!successMessage" variant="success" dismissible @dismissed="successMessage = null">
            {{ successMessage }}
          </b-alert>
          <b-alert :show="!!errorMessage" variant="danger" class="mb-3">
            <div>{{ errorMessage }}</div>
            <b-button
              variant="link"
              size="sm"
              class="p-0 mt-1 sct-nudge"
              :disabled="portalLoading"
              @click="doManageCard"
            >No card? Add one &rarr;</b-button>
          </b-alert>

          <div class="sct-presets mb-3">
            <b-button
              v-for="p in presets"
              :key="p"
              :variant="(!usingCustom && selectedAmount === p) ? 'primary' : 'outline-primary'"
              class="sct-preset"
              @click="selectPreset(p)"
            >${{ p }}</b-button>
          </div>

          <label for="sct-custom-amount" class="sct-label">Or enter a custom amount</label>
          <b-input-group prepend="$" class="mb-2">
            <b-form-input
              id="sct-custom-amount"
              v-model="customAmount"
              type="number"
              :min="MIN_USD"
              :max="MAX_USD"
              step="1"
              placeholder="Amount"
              @focus="usingCustom = true"
            />
          </b-input-group>

          <div class="sct-hint mb-3">
            <template v-if="amountValid">
              ${{ effectiveAmount }} &asymp; {{ creditsForAmount.toLocaleString() }} credits
            </template>
            <template v-else>
              Enter an amount between ${{ MIN_USD }} and ${{ MAX_USD.toLocaleString() }}. $25 = 1,000 credits.
            </template>
          </div>

          <b-button variant="success" block :disabled="!amountValid || buying" @click="doBuy">
            <b-spinner v-if="buying" small class="mr-1" />
            {{ buying ? 'Processing…' : 'Buy now' }}
          </b-button>
          <p class="sct-foot mt-2 mb-0">1 credit = $0.025 &middot; $25 = 1,000 credits</p>
          <p class="sct-foot mb-0">Texts to Puerto Rico numbers use 5 credits per message segment.</p>
        </b-card>
      </b-col>

      <!-- ========================== Buy email credits ========================== -->
      <b-col v-if="emailManageVisible" cols="12" lg="6" class="mb-4">
        <b-card class="h-100 sct-card">
          <h4 class="sct-card__title mb-3">
            <i class="la la-plus-circle mr-2"></i>Buy email credits
          </h4>

          <b-alert :show="!!emailSuccessMessage" variant="success" dismissible @dismissed="emailSuccessMessage = null">
            {{ emailSuccessMessage }}
          </b-alert>
          <b-alert :show="!!emailErrorMessage" variant="danger" class="mb-3">
            <div>{{ emailErrorMessage }}</div>
            <b-button
              variant="link"
              size="sm"
              class="p-0 mt-1 sct-nudge"
              :disabled="portalLoading"
              @click="doManageCard"
            >No card? Add one &rarr;</b-button>
          </b-alert>

          <div class="sct-presets mb-3">
            <b-button
              v-for="p in presets"
              :key="'email-' + p"
              :variant="(!emailUsingCustom && emailSelectedAmount === p) ? 'primary' : 'outline-primary'"
              class="sct-preset"
              @click="emailSelectPreset(p)"
            >${{ p }}</b-button>
          </div>

          <label for="sct-email-custom-amount" class="sct-label">Or enter a custom amount</label>
          <b-input-group prepend="$" class="mb-2">
            <b-form-input
              id="sct-email-custom-amount"
              v-model="emailCustomAmount"
              type="number"
              :min="MIN_USD"
              :max="MAX_USD"
              step="1"
              placeholder="Amount"
              @focus="emailUsingCustom = true"
            />
          </b-input-group>

          <div class="sct-hint mb-3">
            <template v-if="emailAmountValid">
              ${{ emailEffectiveAmount }} &asymp; {{ emailCreditsForAmount.toLocaleString() }} credits
            </template>
            <template v-else>
              Enter an amount between ${{ MIN_USD }} and ${{ MAX_USD.toLocaleString() }}. $20 = {{ emailCreditsForTwenty.toLocaleString() }} credits.
            </template>
          </div>

          <b-button variant="success" block :disabled="!emailAmountValid || emailBuying" @click="doBuyEmail">
            <b-spinner v-if="emailBuying" small class="mr-1" />
            {{ emailBuying ? 'Processing…' : 'Buy now' }}
          </b-button>
          <p class="sct-foot mt-2 mb-0">1 credit = $0.005 &middot; $20 = {{ emailCreditsForTwenty.toLocaleString() }} credits</p>
        </b-card>
      </b-col>

      <!-- Auto-reload — paired with its Email counterpart on the right, so each channel's
           "buy" and "auto-reload" controls sit on the same line as the other channel's. -->
      <b-col cols="12" lg="6" class="mb-4">
        <b-card class="h-100 sct-card">
          <h4 class="sct-card__title mb-2">
            <i class="la la-sync mr-2"></i>Auto-reload
          </h4>

          <!-- REAL (SMS-enabled): editable settings -->
          <template v-if="smsFeatureEnabled">
            <b-form-checkbox v-model="autoReloadEnabled" switch class="sct-arl-switch mb-2">
              Automatically top up my balance
            </b-form-checkbox>
            <p class="text-muted sct-arl-help">
              Keeps you sending without interruption — when your balance runs low we add credits
              to the card on file.
            </p>

            <div v-if="autoReloadEnabled" class="sct-arl-fields">
              <label class="sct-label" for="sct-arl-trigger">Top up when my balance drops below</label>
              <b-input-group prepend="$" class="mb-1">
                <b-form-input
                  id="sct-arl-trigger"
                  v-model="reloadTrigger"
                  @input="reloadTouched = true"
                  type="number"
                  min="1"
                  max="500"
                  step="1"
                  :state="reloadErrors.trigger ? false : null"
                />
              </b-input-group>
              <div v-if="reloadErrors.trigger" class="sct-arl-err">{{ reloadErrors.trigger }}</div>

              <label class="sct-label mt-2" for="sct-arl-amount">Add this much each time</label>
              <b-input-group prepend="$" class="mb-1">
                <b-form-input
                  id="sct-arl-amount"
                  v-model="reloadAmount"
                  @input="reloadTouched = true"
                  type="number"
                  min="5"
                  max="1000"
                  step="1"
                  :state="reloadErrors.amount ? false : null"
                />
              </b-input-group>
              <div v-if="reloadErrors.amount" class="sct-arl-err">{{ reloadErrors.amount }}</div>

              <label class="sct-label mt-2" for="sct-arl-cap">
                Monthly limit <span class="text-muted">(leave blank for no limit)</span>
              </label>
              <b-input-group prepend="$" class="mb-1">
                <b-form-input
                  id="sct-arl-cap"
                  v-model="reloadCap"
                  @input="reloadTouched = true"
                  type="number"
                  min="0"
                  max="10000"
                  step="1"
                  placeholder="No limit"
                  :state="reloadErrors.cap ? false : null"
                />
              </b-input-group>
              <div v-if="reloadErrors.cap" class="sct-arl-err">{{ reloadErrors.cap }}</div>
            </div>

            <b-alert :show="!!reloadSavedMessage" variant="success" class="mt-2 mb-0 py-1 px-2 sct-arl-note">
              {{ reloadSavedMessage }}
            </b-alert>
            <b-alert :show="!!reloadErrorMessage" variant="danger" class="mt-2 mb-0 py-1 px-2 sct-arl-note">
              {{ reloadErrorMessage }}
            </b-alert>

            <b-button
              variant="primary"
              size="sm"
              class="mt-3"
              :disabled="!canSaveReload || savingReload"
              @click="onSaveReload"
            >
              <b-spinner v-if="savingReload" small class="mr-1" />
              {{ savingReload ? 'Saving…' : 'Save' }}
            </b-button>
          </template>

          <!-- PREVIEW (no SMS): original read-only copy — unchanged -->
          <p v-else class="text-muted mb-0">
            Auto-reload keeps you sending: when your balance drops below $5, we automatically add
            $25 of credits to the card on file.
          </p>
        </b-card>
      </b-col>

      <!-- ==================== Email auto-reload ==================== -->
      <b-col v-if="emailManageVisible" cols="12" lg="6" class="mb-4">
        <b-card class="h-100 sct-card">
          <h4 class="sct-card__title mb-2">
            <i class="la la-sync mr-2"></i>Email auto-reload
          </h4>

          <b-form-checkbox v-model="emailAutoReloadEnabled" switch class="sct-arl-switch mb-2">
            Automatically top up my email balance
          </b-form-checkbox>
          <p class="text-muted sct-arl-help">
            Keeps your email campaigns sending without interruption — when your email balance runs
            low we add credits to the card on file.
          </p>

          <div v-if="emailAutoReloadEnabled" class="sct-arl-fields">
            <label class="sct-label" for="sct-email-arl-trigger">Top up when my balance drops below</label>
            <b-input-group prepend="$" class="mb-1">
              <b-form-input
                id="sct-email-arl-trigger"
                v-model="emailReloadTrigger"
                @input="emailReloadTouched = true"
                type="number"
                min="1"
                max="500"
                step="1"
                :state="emailReloadErrors.trigger ? false : null"
              />
            </b-input-group>
            <div v-if="emailReloadErrors.trigger" class="sct-arl-err">{{ emailReloadErrors.trigger }}</div>

            <label class="sct-label mt-2" for="sct-email-arl-amount">Add this much each time</label>
            <b-input-group prepend="$" class="mb-1">
              <b-form-input
                id="sct-email-arl-amount"
                v-model="emailReloadAmount"
                @input="emailReloadTouched = true"
                type="number"
                min="5"
                max="1000"
                step="1"
                :state="emailReloadErrors.amount ? false : null"
              />
            </b-input-group>
            <div v-if="emailReloadErrors.amount" class="sct-arl-err">{{ emailReloadErrors.amount }}</div>

            <label class="sct-label mt-2" for="sct-email-arl-cap">
              Monthly limit <span class="text-muted">(leave blank for no limit)</span>
            </label>
            <b-input-group prepend="$" class="mb-1">
              <b-form-input
                id="sct-email-arl-cap"
                v-model="emailReloadCap"
                @input="emailReloadTouched = true"
                type="number"
                min="0"
                max="10000"
                step="1"
                placeholder="No limit"
                :state="emailReloadErrors.cap ? false : null"
              />
            </b-input-group>
            <div v-if="emailReloadErrors.cap" class="sct-arl-err">{{ emailReloadErrors.cap }}</div>
          </div>

          <b-alert :show="!!emailReloadSavedMessage" variant="success" class="mt-2 mb-0 py-1 px-2 sct-arl-note">
            {{ emailReloadSavedMessage }}
          </b-alert>
          <b-alert :show="!!emailReloadErrorMessage" variant="danger" class="mt-2 mb-0 py-1 px-2 sct-arl-note">
            {{ emailReloadErrorMessage }}
          </b-alert>

          <b-button
            variant="primary"
            size="sm"
            class="mt-3"
            :disabled="!emailCanSaveReload || emailSavingReload"
            @click="onSaveEmailReload"
          >
            <b-spinner v-if="emailSavingReload" small class="mr-1" />
            {{ emailSavingReload ? 'Saving…' : 'Save' }}
          </b-button>
        </b-card>
      </b-col>

      <!-- ======================== Payment method ======================== -->
      <b-col cols="12" lg="6" class="mb-4">
        <b-card class="h-100 sct-card">
          <h4 class="sct-card__title mb-2">
            <i class="la la-credit-card mr-2"></i>Payment method
          </h4>
          <p class="text-muted">
            Add or update the card we use for SMS credits, and view invoices — securely on Stripe.
          </p>
          <b-button variant="outline-dark" :disabled="portalLoading" @click="doManageCard">
            <b-spinner v-if="portalLoading" small class="mr-1" />
            {{ portalLoading ? 'Opening…' : 'Manage payment method' }}
          </b-button>
        </b-card>
      </b-col>

      <!-- ==================== Credit activity (SMS-enabled only) ==================== -->
      <b-col v-if="smsFeatureEnabled" cols="12" lg="6" class="mb-4">
        <b-card class="h-100 sct-card">
          <h4 class="sct-card__title mb-3">
            <i class="la la-history mr-2"></i>Credit activity
          </h4>

          <div v-if="activityLoading && !activity.length" class="text-muted text-center py-3">
            <b-spinner small class="mr-1" />Loading…
          </div>

          <div v-else-if="!activity.length" class="text-muted py-2">
            No credit activity yet.
          </div>

          <div v-else class="table-responsive">
            <table class="table sct-activity mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th class="text-right">Amount</th>
                  <th class="text-right">Receipt</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in activity" :key="row.id">
                  <td class="sct-activity__date">{{ row.createdAt | dateformat(dateFormatStr) }}</td>
                  <td>
                    <div>{{ row.collectedBy }}</div>
                    <div v-if="row.note" class="sct-activity__note">{{ row.note }}</div>
                  </td>
                  <td class="text-right sct-activity__amount">
                    <span class="sct-activity__usd">{{ formatUsd(row.amountUsd) }}</span>
                    <span
                      class="sct-activity__delta"
                      :class="row.creditsDelta > 0 ? 'sct-activity__delta--pos' : 'sct-activity__delta--neg'"
                    >({{ row.creditsDelta > 0 ? '+' : '' }}{{ (row.creditsDelta || 0).toLocaleString() }} credits)</span>
                  </td>
                  <td class="text-right">
                    <span v-if="row.invoiceId" class="sct-activity__receipt">
                      <i class="la la-receipt mr-1"></i>Receipt
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="activityHasMore" class="text-center mt-3">
            <b-button
              variant="outline-secondary"
              size="sm"
              :disabled="activityLoading"
              @click="loadMoreActivity"
            >
              <b-spinner v-if="activityLoading" small class="mr-1" />
              Load more
            </b-button>
          </div>
        </b-card>
      </b-col>

    </b-row>

    <!-- Confirm before turning auto-reload OFF (SMS-enabled sites only). -->
    <b-modal
      v-model="showDisableModal"
      title="Turn off auto-reload?"
      ok-title="Turn off auto-reload"
      ok-variant="danger"
      cancel-title="Keep it on"
      centered
      @hide="onDisableModalHide"
    >
      <p class="mb-0">
        If your balance runs out, some texts to your patients may stop going through — including
        appointment reminders, recall messages, and replies. You can add credits manually anytime.
      </p>
    </b-modal>

    <!-- Confirm before turning EMAIL auto-reload OFF. -->
    <b-modal
      v-model="emailShowDisableModal"
      title="Turn off email auto-reload?"
      ok-title="Turn off auto-reload"
      ok-variant="danger"
      cancel-title="Keep it on"
      centered
      @hide="onEmailDisableModalHide"
    >
      <p class="mb-0">
        If your email balance runs out, some campaign emails to your patients may stop going through.
        You can add credits manually anytime.
      </p>
    </b-modal>
  </div>
</template>

<script>
/**
 * SmsCreditsTab — My Account → "Credits" tab.
 *
 * Leads with a per-channel balances card (SMS · Email today, AI reserved) fed by the
 * shared `wallet` Vuex store (the SAME store that feeds the header CreditBalancePill;
 * `channels.{sms,email}`, loaded on app init by Layout's `wallet/initBalance`). If the
 * balance hasn't loaded yet this tab seeds it. Below the balances, the self-serve SMS
 * top-up controls (buy / card / auto-reload / activity) are always present. A parallel
 * "Manage Email credits" section (buy + auto-reload, targeting the email pool via the
 * store actions' `product:'email'`) appears only when VUE_APP_EMAIL_WALLET_ENABLED is on
 * AND the site has an email pool — otherwise the email row stays display-only ("coming
 * soon"), byte-identical to before. AI purchase + reload arrive later. Buying credits and
 * opening the Stripe billing portal go through the wallet store actions so the balance
 * stays a single source of truth.
 */
import { mapState, mapGetters, mapActions } from 'vuex';
import { v4 as uuidv4 } from 'uuid';
import { validateReloadSettings, lowThresholdCredits } from '@/utils/reloadSettings';
import { PR_SEGMENT_CREDITS } from '@/utils/smsSegments';
import { hasSmsNumber } from '@/utils/siteCapability';
import ChannelIcon from '@/components/ChannelIcon.vue';
import FeatureRequestButton from '@/components/FeatureRequestButton/FeatureRequestButton.vue';
import FeatureRequestNote from '@/components/FeatureRequestButton/FeatureRequestNote.vue';
// Insurance Verification usage-section pure helpers. This component does NOT include the global
// mixin (its parent MyAccount does, but that doesn't reach a child), so the IV flag is read off the
// customer `settings` array here and the checkedAt timestamp is formatted with the IV-purpose-built
// formatCheckedAt (the documented twin of the mixin's formatDateTimeWitTz) rather than the mixin.
import {
  usageMeterView,
  ivUsageSectionVisible,
  usageCardVisible,
  formatCheckedAt,
  ivTriggerLabel,
  ivCheckLogCells,
  ivSplitEntryCount,
  ivSplitBarMax,
  ivUsageBarWidth,
} from '@/pages/InsuranceVerification/components/constants';

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const MIN_USD = 5;
const MAX_USD = 1000;
// Standard monthly free allowance shown in the non-SMS PREVIEW (mirrors the layer default
// SMS_FREE_MONTHLY_CREDITS = 5000). Illustrative only — no per-site wallet query is made.
const PREVIEW_FREE_CREDITS = 5000;

export default {
  name: 'SmsCreditsTab',
  components: { ChannelIcon, FeatureRequestButton, FeatureRequestNote },
  data() {
    return {
      MIN_USD,
      MAX_USD,
      presets: [20, 50, 100],
      selectedAmount: 20,   // active preset ($20 default)
      customAmount: '',     // custom-amount input (wins over the preset when non-empty)
      usingCustom: false,   // the custom field was focused → prefer it over the preset
      buying: false,
      portalLoading: false,
      // Usage report — 'YYYY-MM' or '' for the current billing month. '' is meaningful: only the
      // current month returns a balance/remaining, so the reconciling header line shows only then.
      usageMonthSel: '',
      usageLogOpen: false,
      usageLogLimit: 25,
      // Insurance Verification drill-down page size. iv-prefixed on purpose: usageLogLimit above is
      // the WALLET SMS log's local state — a name coincidence, not the same thing. IV log OPEN /
      // page / rows / trigger all live in the insuranceVerification store, read via iv* getters.
      ivLogLimit: 25,
      successMessage: null,
      errorMessage: null,

      // ---- Auto-reload settings (local form, pre-filled from reloadConfig) ----
      autoReloadEnabled: false,
      reloadTrigger: '',   // $ trigger balance
      reloadAmount: '',    // $ top-up amount
      reloadCap: '',        // $ monthly cap ('' = no limit)
      reloadTouched: false, // user has edited a field → only then surface inline errors
      savingReload: false,
      reloadSavedMessage: null,
      reloadErrorMessage: null,
      showDisableModal: false, // ON→OFF confirmation

      // ---- EMAIL pool controls (flag-gated; mirror the SMS state above) ----
      emailSelectedAmount: 20,
      emailCustomAmount: '',
      emailUsingCustom: false,
      emailBuying: false,
      emailSuccessMessage: null,
      emailErrorMessage: null,
      emailAutoReloadEnabled: false,
      emailReloadTrigger: '',
      emailReloadAmount: '',
      emailReloadCap: '',
      emailReloadTouched: false,
      emailSavingReload: false,
      emailReloadSavedMessage: null,
      emailReloadErrorMessage: null,
      emailShowDisableModal: false,
    };
  },

  computed: {
    ...mapState('wallet', [
      'creditsAvailable',
      'freeAllowance',
      'freeRemainingCredits',
      'paidCredits',
      'creditPriceUsd',
      'resetDate',
      'hasBalance',
      'loading',
      'reloadConfig',
      'activity',
      'activityLoading',
      'activityHasMore',
      'emailReloadConfig',
      'usageSummary',
      'usageSummaryLoading',
      'usageLog',
      'usageLogLoading',
    ]),
    // The WALLET store's own siteID (seeded by initBalance), aliased so it never shadows the
    // CUSTOMER store's `siteID` below. reloadUsage() reads it to tell whether the wallet is seeded
    // before it fires the usage actions, which POST wallet.state.siteID.
    ...mapState('wallet', { walletSiteID: 'siteID' }),
    ...mapState('customer', ['siteID', 'settings']),
    ...mapGetters('customer', ['getSite']),
    ...mapGetters('wallet', ['emailChannel']),
    // Insurance Verification usage panel + drill-down log, aliased with an iv* prefix so nothing
    // collides with the wallet SMS log's `usageLog*` names above.
    ...mapGetters('insuranceVerification', {
      ivUsagePanelData: 'usagePanelData',
      ivUsagePanelLoading: 'usagePanelLoading',
      ivLogRows: 'usageLogRows',
      ivLogOpen: 'usageLogOpen',
      ivLogLoading: 'usageLogLoading',
      ivLogHasMore: 'usageLogHasMore',
      ivLogPage: 'usageLogPage',
      ivLogTotal: 'usageLogTotal',
      ivLogTrigger: 'usageLogTrigger',
    }),

    // ---- Usage report ----------------------------------------------------------------------
    // Which pools to render. Email only appears once the wallet doc actually carries an email
    // pool, mirroring the balances card above so the two never disagree about what exists.
    usageChannels() {
      const out = [{ key: 'sms', label: 'SMS & MMS' }];
      if (this.emailChannel) out.push({ key: 'email', label: 'Email' });
      return out;
    },
    // Current month plus the previous 5, as 'YYYY-MM'. '' (current) is first and is the default,
    // because only the current month reconciles against the header pill.
    usageMonthOptions() {
      const opts = [{ value: '', label: 'This month' }];
      const d = new Date();
      for (let i = 1; i <= 5; i++) {
        const dt = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i, 1));
        const value = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`;
        opts.push({ value, label: dt.toLocaleString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' }) });
      }
      return opts;
    },
    anyUsageLoaded() {
      return this.usageChannels.some((c) => !!this.usageSummary[c.key]);
    },
    // The number in "(N credits per text segment)" — sourced from the shared constant so it can't
    // drift from what the backend actually bills a PR segment at.
    prSegmentCredits() { return PR_SEGMENT_CREDITS; },
    // Outbound-to-Puerto-Rico sub-total for the SMS pool, or null when there is none. PR numbers bill
    // PR_SEGMENT_CREDITS per segment, so a site with PR patients draws more credits than its message
    // count implies; this line names that instead of leaving it spread invisibly across features.
    // Present only for SMS with real PR traffic — most sites never see the concept.
    usagePr() {
      const s = this.usageSummary && this.usageSummary.sms;
      const pr = s && s.pr;
      return pr && Number(pr.messages) > 0 ? pr : null;
    },
    usageLogTitle() {
      const l = this.usageLog;
      const feat = l.feature ? l.feature.replace(/_/g, ' ') : 'all features';
      return `${l.channel === 'email' ? 'Email' : 'SMS'} · ${feat}`;
    },

    // Site-level SMS prerequisite: a provisioned GENERAL_PHONE texting number (same signal
    // every SMS surface uses). When false, this tab renders a static PREVIEW/upsell and the
    // wallet is never queried (Layout skips initBalance, and created() below does too).
    // hasSmsNumber, NOT isSmsEnabled: a WhatsApp-only office still has a wallet (WhatsApp
    // spends credits) — switching outbound SMS off must not turn its credits page into an upsell.
    smsFeatureEnabled() {
      return hasSmsNumber(this.settings);
    },

    // ---- Insurance Verification usage section -------------------------------------------------
    // The practice-enabled IV flag, read off the SAME customer `settings` array the mixin's
    // `insuranceVerificationEnabled` uses (INSURANCE_VERIFICATION_ENABLED === 'TRUE'). Read here
    // rather than via that mixin because this child component doesn't include it.
    ivFlagEnabled() {
      const s = (this.settings || []).find((x) => x && x.Property === 'INSURANCE_VERIFICATION_ENABLED');
      return !!(s && s.PropertyValue === 'TRUE');
    },
    // Site timezone (GENERAL_TIMEZONE) for the checkedAt column; null → formatCheckedAt degrades
    // gracefully (no conversion) rather than throwing.
    siteTimezone() {
      const s = (this.settings || []).find((x) => x && x.Property === 'GENERAL_TIMEZONE');
      return (s && s.PropertyValue) || null;
    },
    // The /usage payload's `counts` (or null until the panel loads).
    ivCounts() {
      return (this.ivUsagePanelData && this.ivUsagePanelData.counts) || null;
    },
    // The shared band/split/plural view. usageMeterView is null-safe → safe zeros before load.
    ivMeter() {
      return usageMeterView(this.ivCounts);
    },
    // Does the IV section render at all? Flag ON, or the site has spent history (survives disable).
    ivUsageVisible() {
      return ivUsageSectionVisible({
        flagEnabled: this.ivFlagEnabled,
        totalPersistedChecks: this.ivMeter.totalPersistedChecks,
      });
    },
    // Does the WHOLE Usage report card render? The card hosts the SMS/email meter AND the IV section,
    // so it shows when EITHER does. Routed through the TESTED usageCardVisible so the shipped gate is
    // the one the gating suite pins (not an inline OR that drifts from the tests).
    showUsageCard() {
      return usageCardVisible({ smsFeatureEnabled: this.smsFeatureEnabled, ivVisible: this.ivUsageVisible });
    },
    // Visible-but-zero vs has-something. Refused-free checks are never persisted (freeChecks > 0)
    // but still count as activity, so they keep the section out of its empty state.
    ivHasUsage() {
      return this.ivMeter.totalPersistedChecks > 0 || this.ivMeter.free > 0;
    },
    // Max bar value across the split entries (each bar width is relative to it).
    ivBarMax() {
      return ivSplitBarMax(this.ivMeter.split);
    },
    // Human label for the currently-selected usage month ('' → "This month"), shown muted in the
    // IV section header the way each wallet channel shows its reconciling line.
    usageMonthLabel() {
      const opt = this.usageMonthOptions.find((m) => m.value === this.usageMonthSel);
      return opt ? opt.label : 'This month';
    },
    // Store rows → rendered drill-down cells, via the pure ivCheckLogCells mapper (the one place
    // that reads the row's `triggerType` / `status` / `billable` / `patientName` / `patNum` keys).
    // Mapping here keeps the template thin AND is what the node-test pins so a key-name drift can't
    // silently blank a column again. `checkedAt` rides along for the date cell (formatted with tz).
    ivLogCells() {
      return this.ivLogRows.map((r) => Object.assign(
        { key: r.verificationId || r.id, checkedAt: r.checkedAt },
        ivCheckLogCells(r),
      ));
    },

    // Illustrative monthly free allowance for the preview (no wallet query for a non-SMS site).
    previewCredits() {
      return PREVIEW_FREE_CREDITS;
    },

    // Display values: the LIVE wallet numbers for an SMS-enabled site; the illustrative
    // "what you'd start with" preview (5,000 free / 0 purchased) otherwise.
    dispCreditsAvailable() {
      return this.smsFeatureEnabled ? this.creditsAvailable : PREVIEW_FREE_CREDITS;
    },
    dispFreeRemaining() {
      return this.smsFeatureEnabled ? this.freeRemainingCredits : PREVIEW_FREE_CREDITS;
    },
    dispFreeAllowance() {
      return this.smsFeatureEnabled ? this.freeAllowance : PREVIEW_FREE_CREDITS;
    },
    dispPaidCredits() {
      return this.smsFeatureEnabled ? this.paidCredits : 0;
    },

    // Share of the monthly FREE allowance still unspent, 0–100, for the SMS tile's progress bar.
    // PRESENTATION ONLY: derived from the two numbers the tile already prints on the line above it
    // ("N of M free") purely so the bar has a width — no new store read, no new API field, and
    // nothing branches on it. Guarded so a site with no allowance (M = 0, e.g. the moment before
    // the wallet lands) draws an empty track instead of NaN%, and clamped so a carried-over
    // remainder can never overflow the track.
    smsFreeRemainingPct() {
      const allowance = num(this.dispFreeAllowance);
      if (allowance <= 0) return 0;
      return Math.max(0, Math.min(100, Math.round((num(this.dispFreeRemaining) / allowance) * 100)));
    },

    // ---- Email channel (separate 10k/mo pool). Present once the wallet doc carries an
    // `email` channel; the balances card renders the row only when it does. ----
    emailAvailable() { return num(this.emailChannel && this.emailChannel.creditsAvailable); },
    emailFreeRemaining() { return num(this.emailChannel && this.emailChannel.freeRemainingCredits); },
    emailFreeAllowance() { return num(this.emailChannel && this.emailChannel.freeAllowance); },
    emailPaidCredits() { return num(this.emailChannel && this.emailChannel.paidCredits); },
    emailResetDate() { return (this.emailChannel && this.emailChannel.resetDate) || null; },

    // ---- Email pool money controls (E3, flag-gated) ----
    // Frontend flag; unset/anything-but-'true' → the email row stays display-only ("coming
    // soon"), the consent modal + campaignGate are unchanged. Mirrors the SMS wallet flag pattern.
    emailWalletEnabled() {
      return process.env.VUE_APP_EMAIL_WALLET_ENABLED === 'true';
    },
    // The "Manage Email credits" section shows only when the flag is on AND the site actually
    // has an email pool (the wallet doc carries `channels.email`).
    emailManageVisible() {
      return this.emailWalletEnabled && !!this.emailChannel;
    },
    // Per-email-credit price from the wallet doc (email pool = $0.005), with a safe default.
    emailCreditPriceUsd() {
      const p = Number(this.emailChannel && this.emailChannel.creditPriceUsd);
      return p > 0 ? p : 0.005;
    },
    // A non-empty custom amount overrides the selected preset (email).
    emailEffectiveAmount() {
      const custom = String(this.emailCustomAmount == null ? '' : this.emailCustomAmount).trim();
      if (custom !== '') return Number(custom);
      return Number(this.emailSelectedAmount);
    },
    emailAmountValid() {
      const n = this.emailEffectiveAmount;
      return Number.isFinite(n) && n >= MIN_USD && n <= MAX_USD;
    },
    emailCreditsForAmount() {
      return Math.round(this.emailEffectiveAmount / this.emailCreditPriceUsd);
    },
    // "$20 = X credits" hint number at the email price (e.g. 4,000 @ $0.005).
    emailCreditsForTwenty() {
      return Math.round(20 / this.emailCreditPriceUsd);
    },
    // Email auto-reload validation (same dollar bounds as SMS — the reload is a $ amount).
    emailReloadValidation() {
      return validateReloadSettings({
        trigger: this.emailReloadTrigger,
        amount: this.emailReloadAmount,
        cap: this.emailReloadCap,
      });
    },
    emailReloadErrors() {
      return (this.emailAutoReloadEnabled && this.emailReloadTouched)
        ? this.emailReloadValidation.errors
        : { trigger: null, amount: null, cap: null };
    },
    emailCanSaveReload() {
      if (!this.emailAutoReloadEnabled) return true;
      return this.emailReloadValidation.valid;
    },

    // Site's preferred date format (mirrors mixin.dateFormatStr / the pill) so the
    // reset date reads MM/DD/YYYY or DD/MM/YYYY per the practice's setting.
    dateFormatStr() {
      const site = this.getSite ? this.getSite(this.siteID) : null;
      return (site && site.DateFormat) || 'MM/DD/YYYY';
    },

    // A non-empty custom amount overrides the selected preset.
    effectiveAmount() {
      const custom = String(this.customAmount == null ? '' : this.customAmount).trim();
      if (custom !== '') return Number(custom);
      return Number(this.selectedAmount);
    },

    amountValid() {
      const n = this.effectiveAmount;
      return Number.isFinite(n) && n >= MIN_USD && n <= MAX_USD;
    },

    creditsForAmount() {
      const price = Number(this.creditPriceUsd) > 0 ? Number(this.creditPriceUsd) : 0.025;
      return Math.round(this.effectiveAmount / price);
    },

    // ---- Auto-reload validation (mirrors the backend bounds) ----
    reloadValidation() {
      return validateReloadSettings({
        trigger: this.reloadTrigger,
        amount: this.reloadAmount,
        cap: this.reloadCap,
      });
    },
    // Only surface inline field errors while the toggle is ON (fields visible) AND the user has
    // actually edited a field — a freshly loaded / untouched form never shows red errors (e.g. when
    // the effective config is below the input minimums, as with dev's tiny env-default amounts).
    reloadErrors() {
      return (this.autoReloadEnabled && this.reloadTouched)
        ? this.reloadValidation.errors
        : { trigger: null, amount: null, cap: null };
    },
    // Saving OFF is always allowed; saving ON requires valid inputs.
    canSaveReload() {
      if (!this.autoReloadEnabled) return true;
      return this.reloadValidation.valid;
    },

    // Credits at/below which a no-auto-reload site counts as "low". The trigger
    // dollars converted to credits at the current price (mirrors reloadSettings util).
    lowThresholdCredits() {
      if (!this.reloadConfig) return 0;
      return lowThresholdCredits(this.reloadConfig.triggerUsd, this.creditPriceUsd);
    },

    // Warn only real SMS sites that have auto-reload OFF and are near empty.
    showLowBalanceBanner() {
      return !!(
        this.smsFeatureEnabled &&
        this.reloadConfig &&
        this.reloadConfig.enabled === false &&
        this.creditsAvailable <= this.lowThresholdCredits
      );
    },
  },

  created() {
    // Seed the balance + load auto-reload settings and credit activity — but ONLY for
    // SMS-enabled sites. A site without texting has no wallet, so we never query it; the
    // tab shows a static preview instead. initBalance commits the wallet siteID (used by
    // the reload/activity actions), so run it first when the balance hasn't loaded.
    if (this.smsFeatureEnabled && this.siteID) {
      if (!this.hasBalance) this.initBalance(this.siteID);
      this.getReloadSettings();
      this.getCreditActivity({ limit: 25, offset: 0 });
      // Email pool auto-reload settings (flag-gated). The "Manage Email credits" section only
      // renders once channels.email is present, but fetching the config is harmless either way.
      if (this.emailWalletEnabled) this.getReloadSettings({ product: 'email' });
      // Usage breakdown for the current month. Cheap (one GROUP BY per channel) and it is the
      // number people arrive asking about, so it loads with the tab rather than on demand.
      this.reloadUsage();
    }

    // Insurance Verification usage panel — fetched REGARDLESS of texting, OUTSIDE the SMS guard
    // above, so an IV-only (no-texting) site still shows its billed-check meter. Ungated on the
    // server (reads our own counters); a not-enabled site simply answers zeros, and the section's
    // own ivUsageVisible gate hides it when there's nothing to show.
    if (this.siteID) {
      this.ivFetchUsageForPanel({ siteID: this.siteID, month: this.usageMonthSel || null });
    }
  },

  mounted() {
    // Arrived from the header pill's "View your usage report" (?view=usage) → bring the section
    // into view. nextTick so the card exists; guarded because scrollIntoView is absent in jsdom
    // and on some older mobile browsers.
    if (this.$route && this.$route.query && this.$route.query.view === 'usage') {
      this.$nextTick(() => {
        const el = document.getElementById('usage-report');
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  },

  watch: {
    // Pre-fill the editable form whenever the persisted config lands or changes.
    reloadConfig: {
      immediate: true,
      handler(cfg) { this.syncReloadForm(cfg); },
    },
    // Same, for the email pool's auto-reload config.
    emailReloadConfig: {
      immediate: true,
      handler(cfg) { this.syncEmailReloadForm(cfg); },
    },

    /**
     * The email pool can appear AFTER the usage report has already loaded, and when it does its
     * breakdown has to be fetched — nothing else will do it.
     *
     * created() fires initBalance() WITHOUT awaiting it and then calls reloadUsage() immediately.
     * reloadUsage() iterates usageChannels, which includes email only once `emailChannel` exists —
     * and emailChannel is derived from the balance payload that is still in flight. Lose that race
     * (a cold page load, where the balance is not already in the store) and email is never fetched
     * at all: the section renders a flat "No email usage in this period" while the API happily
     * returns the real rows. Caught on dev 2026-07-20 by loading the rendered page rather than
     * trusting the endpoint — the SMS half looked perfect, which is exactly what made it easy to miss.
     *
     * Fetch only the email channel here (not a full reloadUsage) so an in-flight SMS request is
     * never cancelled or double-issued, and only when its summary is still missing.
     */
    emailChannel(pool) {
      if (pool && !this.usageSummary.email) {
        this.getUsageSummary({ channel: 'email', month: this.usageMonthSel || null });
      }
    },
  },

  methods: {
    ...mapActions('wallet', [
      'initBalance',
      'buyCredits',
      'openBillingPortal',
      'getReloadSettings',
      'saveReloadSettings',
      'getCreditActivity',
      'getUsageSummary',
      'getUsageLog',
    ]),
    ...mapActions('insuranceVerification', {
      ivFetchUsageForPanel: 'fetchUsageForPanel',
      ivFetchUsageLog: 'fetchUsageLog',
      ivCloseUsageLog: 'closeUsageLog',
    }),

    // ---- Usage report ----------------------------------------------------------------------
    /**
     * Load every visible channel's breakdown for the selected month, plus the Insurance
     * Verification usage panel. Closes BOTH drill-down logs first so a month switch never
     * flashes the previous month's list. The wallet-channel summaries run only for a
     * texting-enabled site (an IV-only site has no wallet); the IV panel always refetches so an
     * IV-only site meters too — the month picker now renders for IV-only sites as well.
     */
    async reloadUsage() {
      // Self-seed the wallet store's siteID before firing the usage actions. getUsageSummary /
      // getUsageLog POST wallet.state.siteID, which is set ONLY by initBalance — but this method is
      // also reached from the month <select @change> and can run while the wallet store is unseeded
      // (a My Account refresh storm, or a site-switch teardown that committed `reset`), which 400s the
      // endpoint (prod Lumigo 2026-09-02). initBalance commits setSiteID SYNCHRONOUSLY before its
      // first await (wallet.js), so a non-awaited call is enough to make the jobs below safe; it is
      // idempotent for the same site.
      if (this.smsFeatureEnabled && this.siteID && !this.walletSiteID) {
        this.initBalance(this.siteID);
      }
      this.closeUsageLog();
      this.ivCloseUsageLog();
      const month = this.usageMonthSel || null;
      const jobs = [];
      if (this.smsFeatureEnabled) {
        jobs.push(...this.usageChannels.map((c) => this.getUsageSummary({ channel: c.key, month })));
      }
      if (this.siteID) jobs.push(this.ivFetchUsageForPanel({ siteID: this.siteID, month }));
      await Promise.all(jobs);
    },
    /**
     * One row per RENDERED identity — i.e. per (feature, direction).
     *
     * The endpoint groups by Feature + SendMode + Direction + MessageType, but this card renders
     * only featureLabel(row), which reads feature + direction and nothing else. So an inbound SMS
     * and an inbound MMS arrive as two rows that both render "Patient replies received", and an
     * attended and an unattended tx_presenter both render "Tx presenter" — the report looked like
     * it had duplicate rows (seen on prod 2026-07-21: 59 and 3).
     *
     * Merging is the correct fix rather than a cosmetic one, because openUsageLog() already drills
     * down on feature + direction ALONE: clicking either duplicate opened the exact same message
     * list. The summary was disagreeing with the log it opens. Collapsing here makes the two agree
     * and keeps the drill-down working untouched.
     *
     * Re-sorted after merging — summing changes the ordering the server produced.
     */
    usageRows(channel) {
      const s = this.usageSummary[channel];
      const raw = (s && s.breakdown) || [];
      const merged = new Map();
      for (const r of raw) {
        const key = `${r.feature}|${r.direction}`;
        const hit = merged.get(key);
        if (hit) {
          hit.credits += Number(r.credits) || 0;
          hit.messages += Number(r.messages) || 0;
        } else {
          // Only the fields this card and openUsageLog() actually use — carrying sendMode /
          // messageType through a merged row would be a value that is no longer true of it.
          merged.set(key, {
            feature: r.feature,
            direction: r.direction,
            credits: Number(r.credits) || 0,
            messages: Number(r.messages) || 0,
          });
        }
      }
      return [...merged.values()].sort((a, b) => b.credits - a.credits);
    },
    /**
     * The reconciling line. Present ONLY for the current month — the backend deliberately returns
     * no balance for a past month, since "remaining" is not a real thing in the past.
     */
    usageHeadline(channel) {
      const s = this.usageSummary[channel];
      if (!s) return '';
      const used = (s.creditsUsed || 0).toLocaleString();
      if (!s.balance) return `${used} credits used`;
      const b = s.balance;
      const free = Number(b.freeRemaining) || 0;
      const avail = Number(b.creditsAvailable) || 0;
      const paid = Math.max(0, avail - free);
      // LEAD WITH THE PILL'S NUMBER. This used to show free-remaining only, which silently
      // contradicted the header pill for any site holding purchased credits — the pill shows
      // creditsAvailable (free + purchased). It read fine everywhere until a wallet had a paid
      // balance, because free == available while the paid pool is empty. When there IS a paid
      // balance we show the split, so the two components are visible rather than mysterious.
      const base = `${used} used · ${avail.toLocaleString()} available`;
      return paid > 0
        ? `${base} (${free.toLocaleString()} free + ${paid.toLocaleString()} purchased)`
        : `${base} of ${(Number(b.freeAllowance) || 0).toLocaleString()} free`;
    },
    usageBarWidth(channel, row) {
      const rows = this.usageRows(channel);
      const max = rows.reduce((m, r) => Math.max(m, r.credits || 0), 0);
      if (!max) return '0%';
      return `${Math.max(2, Math.round((row.credits / max) * 100))}%`;
    },
    /** 'appointment_reminder' → 'Appointment reminder'; inbound gets a plain-English label. */
    featureLabel(row) {
      if (row.direction === 'in') return 'Patient replies received';
      const f = String(row.feature || 'unknown').replace(/_/g, ' ');
      return f.charAt(0).toUpperCase() + f.slice(1);
    },
    async openUsageLog(channel, row) {
      this.usageLogOpen = true;
      await this.getUsageLog({
        channel,
        month: this.usageMonthSel || null,
        feature: row.feature,
        direction: row.direction,
        page: 1,
        limit: this.usageLogLimit,
      });
    },
    closeUsageLog() { this.usageLogOpen = false; },
    async usagePage(delta) {
      const next = Math.max(1, (this.usageLog.page || 1) + delta);
      await this.getUsageLog({
        channel: this.usageLog.channel,
        month: this.usageMonthSel || null,
        feature: this.usageLog.feature,
        direction: this.usageLog.direction,
        page: next,
        limit: this.usageLogLimit,
      });
    },

    // ---- Insurance Verification usage section -------------------------------------------------
    // Both the printed number and the bar width for a split entry are `total ?? billable`, now a
    // tested pure helper (ivSplitEntryCount) so "total wins when present" can't silently regress.
    ivEntryCount(entry) {
      return ivSplitEntryCount(entry);
    },
    ivBarWidth(entry) {
      return ivUsageBarWidth(ivSplitEntryCount(entry), this.ivBarMax);
    },
    // Open the per-check drill-down for one trigger. keyMap turns the split entry's camelCase key
    // into the backend trigger string the store/endpoint expect.
    openIvLog(entry) {
      const keyMap = { nightly: 'nightly', onDemand: 'on_demand', onBooking: 'on_booking' };
      this.ivFetchUsageLog({
        siteID: this.siteID,
        month: this.usageMonthSel || null,
        trigger: keyMap[entry.key] || null,
        page: 1,
        limit: this.ivLogLimit,
      });
    },
    ivUsagePage(delta) {
      const next = Math.max(1, (this.ivLogPage || 1) + delta);
      this.ivFetchUsageLog({
        siteID: this.siteID,
        month: this.usageMonthSel || null,
        trigger: this.ivLogTrigger,
        page: next,
        limit: this.ivLogLimit,
      });
    },
    // ivTriggerLabelFor stays for the log TITLE (the active filter, not a row). Row cells go through
    // the ivCheckLogCells mapper (see the ivLogCells computed) so the template stays thin and the
    // row field keys are pinned in one tested place.
    ivTriggerLabelFor(trigger) { return ivTriggerLabel(trigger); },
    // Shared date formatter for BOTH drill-down logs. checkedAt (IV rows, ISO-Z UTC) and createdAt
    // (SMS rows, a UTC MySQL DATETIME) are both stored UTC; formatCheckedAt treats them as UTC and
    // converts to the site timezone, handling naive-MySQL and ISO-Z alike.
    formatUsageLogDate(value) { return formatCheckedAt(value, this.siteTimezone); },

    // Copy the persisted config into the local form fields (blank cap when null). Resets the
    // touched flag so the freshly-loaded form shows no red errors until the user edits a field.
    syncReloadForm(cfg) {
      if (!cfg) return;
      this.autoReloadEnabled = !!cfg.enabled;
      this.reloadTrigger = cfg.triggerUsd != null ? cfg.triggerUsd : '';
      this.reloadAmount = cfg.amountUsd != null ? cfg.amountUsd : '';
      this.reloadCap = cfg.monthlyCapUsd != null ? cfg.monthlyCapUsd : '';
      this.reloadTouched = false;
    },

    // Save clicked. Turning auto-reload OFF (was ON) confirms first; otherwise save now.
    onSaveReload() {
      if (this.savingReload) return;
      const wasEnabled = !!(this.reloadConfig && this.reloadConfig.enabled);
      if (wasEnabled && !this.autoReloadEnabled) {
        this.showDisableModal = true;
        return;
      }
      this.performSaveReload();
    },

    // Modal closed: OK button → proceed with save(off); anything else → revert the toggle.
    onDisableModalHide(bvEvent) {
      if (bvEvent && bvEvent.trigger === 'ok') {
        this.performSaveReload();
      } else {
        this.autoReloadEnabled = true;
      }
    },

    async performSaveReload() {
      if (this.savingReload) return;
      this.reloadSavedMessage = null;
      this.reloadErrorMessage = null;
      // Guard: never save invalid values while enabling.
      if (this.autoReloadEnabled && !this.reloadValidation.valid) return;

      this.savingReload = true;
      const capStr = String(this.reloadCap == null ? '' : this.reloadCap).trim();
      const toNum = (v) => (v === '' || v == null ? null : Number(v));
      const payload = {
        autoReloadEnabled: this.autoReloadEnabled,
        reloadTriggerUsd: toNum(this.reloadTrigger),
        reloadAmountUsd: toNum(this.reloadAmount),
        reloadMonthlyCapUsd: capStr === '' ? null : Number(capStr),
      };
      try {
        const res = await this.saveReloadSettings(payload);
        if (res && res.ok) {
          this.reloadSavedMessage = 'Auto-reload settings saved.';
          // Re-fetch so the form reflects any server-normalized values (getReloadSettings
          // commits setReloadConfig → the watcher re-syncs the form).
          await this.getReloadSettings();
          setTimeout(() => { this.reloadSavedMessage = null; }, 4000);
        } else {
          this.reloadErrorMessage = (res && res.message) || 'Could not save auto-reload settings. Please try again.';
        }
      } catch (err) {
        this.reloadErrorMessage = (err && err.message) || 'Could not save auto-reload settings. Please try again.';
      } finally {
        this.savingReload = false;
      }
    },

    loadMoreActivity() {
      if (this.activityLoading) return;
      this.getCreditActivity({ limit: 25, offset: this.activity.length });
    },

    // A transaction's dollar value, e.g. "$20.00". Direction is conveyed by creditsDelta.
    formatUsd(usd) {
      const n = Number(usd) || 0;
      return '$' + Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    selectPreset(amount) {
      this.selectedAmount = amount;
      this.customAmount = '';
      this.usingCustom = false;
    },

    async doBuy() {
      if (!this.amountValid || this.buying) return;
      this.successMessage = null;
      this.errorMessage = null;
      this.buying = true;
      // Unique idempotency key per Buy click — a retry/double-click reuses none.
      const purchaseId = uuidv4();
      const amountUsd = this.effectiveAmount;
      const added = this.creditsForAmount;
      try {
        const res = await this.buyCredits({ amountUsd, purchaseId });
        if (res && res.ok) {
          // setBalance already committed → creditsAvailable reflects the new total.
          this.successMessage =
            `Added ${added.toLocaleString()} credits — new balance ${this.creditsAvailable.toLocaleString()}.`;
        } else {
          this.errorMessage = (res && res.message) || 'Purchase failed. Please try again.';
        }
      } catch (err) {
        this.errorMessage = (err && err.message) || 'Purchase failed. Please try again.';
      } finally {
        this.buying = false;
      }
    },

    async doManageCard() {
      if (this.portalLoading) return;
      this.errorMessage = null;
      this.portalLoading = true;
      try {
        // The server checks WHO is asking (an Admin of this site), so the Firebase token goes with the call.
        const res = await this.openBillingPortal({ auth: this.$store.state.getAuth() });
        // On success the browser redirects away; only handle a failure here.
        if (res && !res.ok) {
          this.errorMessage = res.message || 'Could not open the billing portal. Please try again.';
        }
      } catch (err) {
        this.errorMessage = (err && err.message) || 'Could not open the billing portal. Please try again.';
      } finally {
        this.portalLoading = false;
      }
    },

    // ---- EMAIL pool controls (mirror the SMS methods; buy/reload target product:'email') ----
    emailSelectPreset(amount) {
      this.emailSelectedAmount = amount;
      this.emailCustomAmount = '';
      this.emailUsingCustom = false;
    },

    async doBuyEmail() {
      if (!this.emailAmountValid || this.emailBuying) return;
      this.emailSuccessMessage = null;
      this.emailErrorMessage = null;
      this.emailBuying = true;
      const purchaseId = uuidv4();
      const amountUsd = this.emailEffectiveAmount;
      const added = this.emailCreditsForAmount;
      try {
        const res = await this.buyCredits({ amountUsd, purchaseId, product: 'email' });
        if (res && res.ok) {
          // setBalance committed the full summary → channels.email (emailAvailable) is fresh.
          this.emailSuccessMessage =
            `Added ${added.toLocaleString()} email credits — new balance ${this.emailAvailable.toLocaleString()}.`;
        } else {
          this.emailErrorMessage = (res && res.message) || 'Purchase failed. Please try again.';
        }
      } catch (err) {
        this.emailErrorMessage = (err && err.message) || 'Purchase failed. Please try again.';
      } finally {
        this.emailBuying = false;
      }
    },

    // Copy the persisted EMAIL config into the local form fields (blank cap when null).
    syncEmailReloadForm(cfg) {
      if (!cfg) return;
      this.emailAutoReloadEnabled = !!cfg.enabled;
      this.emailReloadTrigger = cfg.triggerUsd != null ? cfg.triggerUsd : '';
      this.emailReloadAmount = cfg.amountUsd != null ? cfg.amountUsd : '';
      this.emailReloadCap = cfg.monthlyCapUsd != null ? cfg.monthlyCapUsd : '';
      this.emailReloadTouched = false;
    },

    onSaveEmailReload() {
      if (this.emailSavingReload) return;
      const wasEnabled = !!(this.emailReloadConfig && this.emailReloadConfig.enabled);
      if (wasEnabled && !this.emailAutoReloadEnabled) {
        this.emailShowDisableModal = true;
        return;
      }
      this.performSaveEmailReload();
    },

    onEmailDisableModalHide(bvEvent) {
      if (bvEvent && bvEvent.trigger === 'ok') {
        this.performSaveEmailReload();
      } else {
        this.emailAutoReloadEnabled = true;
      }
    },

    async performSaveEmailReload() {
      if (this.emailSavingReload) return;
      this.emailReloadSavedMessage = null;
      this.emailReloadErrorMessage = null;
      if (this.emailAutoReloadEnabled && !this.emailReloadValidation.valid) return;

      this.emailSavingReload = true;
      const capStr = String(this.emailReloadCap == null ? '' : this.emailReloadCap).trim();
      const toNum = (v) => (v === '' || v == null ? null : Number(v));
      const payload = {
        product: 'email',
        autoReloadEnabled: this.emailAutoReloadEnabled,
        reloadTriggerUsd: toNum(this.emailReloadTrigger),
        reloadAmountUsd: toNum(this.emailReloadAmount),
        reloadMonthlyCapUsd: capStr === '' ? null : Number(capStr),
      };
      try {
        const res = await this.saveReloadSettings(payload);
        if (res && res.ok) {
          this.emailReloadSavedMessage = 'Email auto-reload settings saved.';
          await this.getReloadSettings({ product: 'email' });
          setTimeout(() => { this.emailReloadSavedMessage = null; }, 4000);
        } else {
          this.emailReloadErrorMessage = (res && res.message) || 'Could not save auto-reload settings. Please try again.';
        }
      } catch (err) {
        this.emailReloadErrorMessage = (err && err.message) || 'Could not save auto-reload settings. Please try again.';
      } finally {
        this.emailSavingReload = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* The shared My Account tab design language — the same tokens Patient Payments and
   Insurance Verification already ship (2026-08-18 "Option A" re-skin, see _tokens.scss). */
@import './PatientPayments/tokens';

/* Channel accents. SMS uses the shared brand. Email and AI have to stay visually
   DISTINCT from it and from each other — three identical teal chips would make the
   three balance rows unscannable — so they get FLAT single-hue tints in the same
   tonal register as $pp-brand-subtle/$pp-brand: a desaturated indigo for Email (the
   design system's "iris" family, see the _tokens.scss header) and a quiet neutral
   slate for AI, which is deliberately the least loud of the three because AI credits
   are not purchasable yet. */
$sct-email:        #5b5bd6;
$sct-email-subtle: #ecebfb;
$sct-ai:           #6b7680;
$sct-ai-subtle:    #eef1f4;

.sms-credits-tab {
  max-width: 960px;
}

/* ============================================
   TAB HEADER (.ma-tabhead) — eyebrow / display title / lede
   Byte-for-byte the block MyAccount.vue's Team tab declares, class names included: both are
   the same component of the same design language, so a change is one find across two files.
   They stay two SCOPED copies rather than one global rule because this repo's CSS is scoped
   by default and a global .ma-tabhead would be live on every page of the app.

   24px, not Patient Payments' 34px: this is a tab inside a route that already carries its own
   "My Account" <h2>, and the page scale would compete with it.
   ============================================ */
.ma-tabhead {
  margin-bottom: 20px;
}
.ma-tabhead__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: $pp-brand;
  margin-bottom: 8px;
}
.ma-tabhead__title {
  margin: 0;
  font-family: $pp-font-display;
  font-weight: 800;
  font-size: 24px;
  letter-spacing: -0.03em;
  line-height: 1.05;
  color: $pp-text;
}
.ma-tabhead__lede {
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: $pp-text-muted;
  max-width: 660px;
}

/* ---- Preview / upsell (site without SMS texting enabled) ---- */
.sct-upsell {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.1rem 1.25rem;
  margin-bottom: 1.25rem;
  border: 1px solid $pp-brand-border;
  border-radius: $pp-radius;
  background: $pp-brand-subtle;
}
.sct-upsell__icon {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: $pp-radius-sm;
  background: $pp-brand;
  color: #fff;
  font-size: 1.4rem;
}
.sct-upsell__body { flex: 1 1 auto; min-width: 0; }
.sct-upsell__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: $pp-text-strong;
  margin: 0 0 0.25rem;
}
.sct-upsell__desc {
  font-size: 0.85rem;
  color: $pp-text-muted;
  line-height: 1.5;
  margin: 0 0 0.75rem;
  max-width: 640px;
}
.sct-upsell__cta {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem; // icon to label (the icon touched the text)
  padding: 0.5rem 0.9rem;
  border-radius: $pp-radius-sm;
  background: $pp-brand;
  color: #fff;
  font-weight: 700;
  font-size: 0.85rem;
  text-decoration: none;
}
.sct-upsell__cta:hover { background: $pp-brand-deep; color: #fff; text-decoration: none; }

/* Cards below the banner are a non-interactive PREVIEW of the real feature. */
.sct-preview-lock {
  opacity: 0.55;
  pointer-events: none;
  user-select: none;
}

.sct-card {
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: $pp-radius;
  box-shadow: $pp-shadow;
}

.sct-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sct-card__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: $pp-text-strong;
}
.sct-card__title .la {
  color: $pp-brand;
}

/* ---- Per-channel balances (SMS · Email · AI) ----
   The PatientPayments .pp-stat anatomy, one tile per channel: quiet uppercase label with the
   channel chip, the big display figure under it, the muted sub-line, and (SMS only) the
   allowance bar. auto-fit + minmax(220px, 1fr) is what makes this responsive by default — the
   tiles REFLOW to fewer columns and finally to one as the card narrows, so nothing overflows;
   this card is only half the row above lg and full width below it. */
.sct-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-top: 10px;
}
.sct-chan {
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: $pp-radius;
  box-shadow: $pp-shadow;
  padding: 18px 20px;
  /* Grid items default to min-width:auto, which lets a long sub-line push the tile wider than
     its track. This is what keeps the reflow a WRAP rather than a horizontal overflow. */
  min-width: 0;
}
.sct-chan--muted {
  opacity: 0.7;
}
/* Label row: the channel chip sits beside its name rather than beside the figure, so the tile
   reads label → figure → detail top to bottom like every other stat tile in the product. */
.sct-chan__label {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}
/* Flat tinted chips, not gradients — the tint carries the channel, the icon carries
   the hue. Same construction as PaymentTable's .pp-avatar, sized down from 40px to 28px:
   in a tile the chip annotates the label, it is no longer the row's leading element. */
.sct-chan__ic {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: $pp-radius-sm;
  font-size: 0.95rem;
}
.sct-chan__ic--sms { background: $pp-brand-subtle; color: $pp-brand; }
.sct-chan__ic--email { background: $sct-email-subtle; color: $sct-email; }
.sct-chan__ic--ai { background: $sct-ai-subtle; color: $sct-ai; }
/* The design language's stat label: 11px uppercase muted (PaymentStatCards .pp-stat-label). */
.sct-chan__name {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: $pp-text-muted;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}
.sct-chan__tag {
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $pp-text-muted;
  background: $pp-surface-sunken;
  border: 1px solid $pp-border;
  border-radius: $pp-radius-pill;
  padding: 2px 7px;
}
.sct-chan__tag--email {
  color: $sct-email;
  background: $sct-email-subtle;
  border-color: $sct-email-subtle;
}
.sct-chan__sub {
  font-size: 12.5px;
  color: $pp-text-muted;
  margin-top: 8px;
  line-height: 1.45;
}
.sct-chan__sub b {
  color: $pp-text-strong;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
/* The display figure, same construction as PaymentStatCards' .pp-stat-figure (that one is
   38px on a card of its own; 30px is the tile scale here, where three tiles share a
   half-width card). Tabular figures so the balances line up column-to-column and don't
   jitter as they change. */
.sct-chan__bal {
  font-family: $pp-font-display;
  font-weight: 800;
  font-size: 30px;
  letter-spacing: -0.03em;
  color: $pp-text;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  margin-top: 12px;
}
.sct-chan__bal--muted {
  color: $pp-border-strong;
}
.sct-chan__unit {
  font-size: 0.68rem;
  color: $pp-text-subtle;
  margin-top: 4px;
}
/* Allowance bar — PaymentStatCards' .pp-bar construction (6px brand fill on a brand-subtle
   track). Kept separate from the usage report's .sct-usage__bar, which is 8px and is HIDDEN
   below 576px; this one is the tile's own and stays visible at every width. */
.sct-chan__bar {
  height: 6px;
  border-radius: 6px;
  background: $pp-brand-subtle;
  margin-top: 14px;
  overflow: hidden;
}
.sct-chan__barfill {
  height: 100%;
  background: $pp-brand;
  border-radius: 6px;
  transition: width 0.3s ease;
}

/* ---- "Manage SMS credits" section header ---- */
.sct-section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin: 0.25rem 0 0.9rem;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid $pp-border;
}
.sct-section-head > span:first-child {
  font-size: 0.9rem;
  font-weight: 800;
  color: $pp-text-strong;
}
.sct-section-soon {
  font-size: 0.72rem;
  color: $pp-text-subtle;
  font-weight: 600;
}

/* Very small screens: drop the per-credit unit line so the balance number stays clean. */
@media (max-width: 420px) {
  .sct-chan__unit { display: none; }
}

.sct-foot {
  margin-top: 14px;
  font-size: 0.72rem;
  color: $pp-text-subtle;
}

.sct-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.sct-preset {
  flex: 1 1 auto;
  min-width: 72px;
  font-weight: 700;
}

/* ---- Buttons ----
   BootstrapVue's variants are restyled to the design language's .pp-btn rather than
   replaced, so nothing about the markup or behaviour changes. Every selector is
   nested under .sms-credits-tab and therefore compiles WITH this component's scope
   attribute — a b-button's ROOT element carries the parent's scope id, so no
   ::v-deep is needed and nothing here can reach another page (repo CSS rule). */
.sms-credits-tab {
  .btn {
    border-radius: $pp-radius-sm;
    font-weight: 600;
  }

  // Primary + the buy CTAs (variant="success") are the same action in the design
  // language: one solid brand button.
  .btn-primary,
  .btn-success {
    background-color: $pp-brand;
    border-color: $pp-brand;
    color: #fff;

    &:hover:not(:disabled),
    &:focus:not(:disabled),
    &:not(:disabled):not(.disabled):active {
      background-color: $pp-brand-deep;
      border-color: $pp-brand-deep;
      color: #fff;
    }
  }

  // Unselected buy presets and the pager/receipt buttons: quiet outlines.
  .btn-outline-primary,
  .btn-outline-secondary,
  .btn-outline-dark {
    border-color: $pp-border-strong;
    color: $pp-text-strong;
    background-color: $pp-surface;

    &:hover:not(:disabled),
    &:focus:not(:disabled) {
      background-color: $pp-surface-sunken;
      border-color: $pp-border-strong;
      color: $pp-text-strong;
    }
  }

  // The SELECTED preset is the primary rule above (variant flips to 'primary'),
  // so nothing extra is needed for it.

  .btn-link {
    color: $pp-brand;

    &:hover { color: $pp-brand-deep; }
  }
}

.sct-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: $pp-text-muted;
  margin-bottom: 0.25rem;
}
.sct-hint {
  font-size: 0.78rem;
  color: $pp-text-muted;
}
.sct-nudge {
  font-weight: 600;
}

/* ---- Low-balance warning banner ---- */
.sct-lowbal {
  border-radius: $pp-radius-sm;
  font-size: 0.86rem;
}

/* ---- Auto-reload settings ---- */
.sct-arl-switch {
  font-weight: 600;
  color: $pp-text-strong;
}
.sct-arl-help {
  font-size: 0.8rem;
  line-height: 1.45;
  margin-bottom: 0.75rem;
}
.sct-arl-fields {
  margin-top: 0.25rem;
}
.sct-arl-err {
  font-size: 0.74rem;
  color: $pp-red-text;
  margin-bottom: 0.25rem;
}
.sct-arl-note {
  font-size: 0.8rem;
}

/* ---- Credit activity table ---- */
.sct-activity {
  font-size: 0.85rem;
}
/* Table language matches PaymentTable's .pp-head/.pp-row: a flat sunken header with a
   quiet uppercase label, and rows divided by a single hairline. */
.sct-activity thead th {
  background: $pp-surface-sunken;
  border-top: 0;
  border-bottom: 1px solid $pp-border;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: $pp-text-muted;
  font-weight: 700;
}
.sct-activity td {
  vertical-align: middle;
  border-top: 1px solid $pp-border;
}
.sct-activity__date {
  white-space: nowrap;
  color: $pp-text-muted;
}
.sct-activity__note {
  font-size: 0.74rem;
  color: $pp-text-subtle;
  margin-top: 2px;
}
.sct-activity__amount {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.sct-activity__usd {
  font-weight: 700;
  color: $pp-text;
  margin-right: 0.35rem;
}
.sct-activity__delta {
  font-size: 0.78rem;
}
/* Credits ADDED read as the brand tone (money in); credits spent stay muted — a
   purchase isn't a "success" needing green, and spend isn't an error needing red. */
.sct-activity__delta--pos {
  color: $pp-brand-deep;
}
.sct-activity__delta--neg {
  color: $pp-text-muted;
}
.sct-activity__receipt {
  color: $pp-brand;
  font-weight: 600;
  white-space: nowrap;
}

/* ---- Usage report ----
   All scoped (this markup renders inside the page, not a portaled popover), so nothing here can
   reach another page — per the repo's scoped-CSS rule. */
.sct-usage__monthpick { display: flex; align-items: center; font-size: 0.8rem; color: $pp-text-muted; }
.sct-usage__select {
  border: 1px solid $pp-border-strong; border-radius: 6px; padding: 3px 8px;
  font-size: 0.8rem; color: $pp-text-strong; background: $pp-surface;

  &:focus { border-color: $pp-brand; box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12); outline: none; }
}
/* SMS and Email side by side on desktop so the card is half as tall and the right side of the page
   isn't dead space; they stack below lg where two columns would squeeze the bars. */
/* ONE channel per line, always.
   This was `1fr 1fr` above lg, from when the usage card spanned the full page. The card now sits
   half-width beside the balances, so two channel columns left each one ~278px — and a breakdown row
   reserves 120px label + 54px count + 62px messages + 30px gaps = 266px before the bar gets any
   width at all. The result was measurable: every bar computed to exactly 0px, on both channels. The
   bars are the report, so the channels stack instead. Below lg the card goes full width and this is
   still correct — a single wide column reads better than two cramped ones. */
.sct-usage__grid { display: grid; grid-template-columns: 1fr; gap: 22px; }
.sct-usage__chhead {
  display: flex; flex-wrap: wrap; align-items: baseline; gap: 8px;
  padding-bottom: 6px; border-bottom: 1px solid $pp-border; margin-bottom: 10px;
}
.sct-usage__chname { font-weight: 700; font-size: 0.9rem; color: $pp-text-strong; }
.sct-usage__chline { font-size: 0.78rem; color: $pp-text-muted; }
.sct-usage__empty { font-size: 0.85rem; }

.sct-usage__bars { display: flex; flex-direction: column; gap: 6px; }
/* Each breakdown row is a button (it opens the message log) styled as a table row. */
.sct-usage__row {
  display: grid; grid-template-columns: minmax(120px, 1.4fr) 3fr auto auto;
  align-items: center; gap: 10px;
  background: none; border: 0; padding: 4px 6px; width: 100%; text-align: left;
  border-radius: 6px; cursor: pointer; font-family: inherit;
}
.sct-usage__row:hover { background: $pp-surface-sunken; }
.sct-usage__label { font-size: 0.83rem; color: $pp-text-strong; }
/* Bar in the design language's own construction (PaymentStatCards .pp-bar): a
   brand-subtle track with a solid brand fill. */
.sct-usage__bar { height: 8px; background: $pp-brand-subtle; border-radius: 4px; overflow: hidden; }
.sct-usage__fill { display: block; height: 100%; background: $pp-brand; border-radius: 4px; }
.sct-usage__num { font-size: 0.83rem; font-weight: 700; color: $pp-text-strong; text-align: right; min-width: 54px; }
.sct-usage__msgs { font-size: 0.72rem; color: $pp-text-subtle; text-align: right; min-width: 62px; }

/* Puerto Rico sub-total line under the SMS breakdown. Scoped like everything else in
   this section. It stays a distinct CALLOUT (tinted panel + bold label) but on the
   brand tone rather than its own blue — one accent colour per surface. */
.sct-usage__pr {
  display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px 10px;
  margin-top: 10px; padding: 6px 10px;
  background: $pp-brand-subtle; border: 1px solid $pp-brand-border; border-radius: 6px;
  font-size: 0.8rem; color: $pp-text-strong;
}
.sct-usage__pr-label { font-weight: 700; color: $pp-brand-deep; }
.sct-usage__pr-label .la { color: $pp-brand-deep; }
.sct-usage__pr-nums { font-variant-numeric: tabular-nums; }
.sct-usage__pr-note { color: $pp-text-muted; font-size: 0.72rem; }
/* Small "PR" pill on a Puerto Rico drill-down log row. */
.sct-usage__prbadge {
  display: inline-block; margin-left: 6px; padding: 0 5px;
  font-size: 0.62rem; font-weight: 800; letter-spacing: 0.03em;
  color: $pp-brand-deep; background: $pp-brand-subtle; border-radius: $pp-radius-pill; vertical-align: middle;
}


.sct-usage__log { margin-top: 16px; border-top: 1px solid $pp-border; padding-top: 12px; }
.sct-usage__loghead {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 0.82rem; font-weight: 700; color: $pp-text-strong; margin-bottom: 8px;
}
.sct-usage__logcount { font-weight: 400; color: $pp-text-subtle; margin-left: 6px; }
.sct-usage__close { background: none; border: 0; padding: 0; color: $pp-text-muted; font-size: 0.78rem; cursor: pointer; font-family: inherit; }
.sct-usage__close:hover { color: $pp-text-strong; text-decoration: underline; }
.sct-usage__table { width: 100%; font-size: 0.82rem; }
.sct-usage__table thead th {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.07em;
  color: $pp-text-muted; font-weight: 700; border-bottom: 1px solid $pp-border; padding: 4px 6px;
}
.sct-usage__table td { padding: 4px 6px; border-bottom: 1px solid $pp-border; color: $pp-text-strong; }
.sct-usage__pager { display: flex; align-items: center; justify-content: center; margin-top: 10px; }

/* ---- Insurance Verification usage section (scoped, sct-ivu__ prefix). Reuses sct-usage__* for
   the header/bars/log so it reads as one card with the wallet channels; only the bits that differ
   get their own class. ---- */
.sct-ivu__month { margin-left: auto; font-size: 0.78rem; color: $pp-text-subtle; }
/* IV rows have no "messages" column, so a 3-track grid (label · bar · count) rather than the
   wallet channels' 4-track one. */
.sct-ivu__row { grid-template-columns: minmax(120px, 1.4fr) 3fr auto; }
.sct-ivu__free {
  display: flex; align-items: baseline; justify-content: space-between; gap: 10px;
  margin-top: 8px; padding: 4px 6px; font-size: 0.83rem;
}
.sct-ivu__free-label { color: $pp-text-muted; }
.sct-ivu__free-label .la { color: $pp-text-subtle; }
.sct-ivu__free-num { font-weight: 700; color: $pp-text-strong; font-variant-numeric: tabular-nums; }
.sct-ivu__note { font-size: 0.76rem; margin-top: 8px; }

/* Responsive by default: on narrow screens the bar column is the first thing worth dropping —
   the label and the numbers still carry the whole meaning. */
@media (max-width: 575.98px) {
  .sct-usage__row { grid-template-columns: 1fr auto auto; }
  .sct-ivu__row { grid-template-columns: 1fr auto; }
  .sct-usage__bar { display: none; }
  .sct-usage__chhead { gap: 2px; }
}
</style>
